import * as actions from './contact-profile.actions';
import { ContactProfileActions } from './contact-profile.actions';
import * as fromRoot from '../../store/app.reducers';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { HttpStatus } from '../../constants/HttpStatusCodes';
import * as formControls from '../../shared/utils/form-controls';
import { ApiException } from '@app/clients/client.generated';

export interface ContactProfileState {
    isPatching: boolean;
    isChangingPassword: boolean;
    isLoadingProfile: boolean;
    isUpdatingProfile: boolean;
    isAcceptingTerms: boolean;
    profile: any;
    errors: ContactError[];
}

const initialState = {
    isPatching: false,
    isChangingPassword: false,
    isLoadingProfile: false,
    isUpdatingProfile: false,
    isAcceptingTerms: false,
    profile: null,
    errors: []
};

export interface State extends fromRoot.AppState {
    contactProfile: ContactProfileState;
}


export function reducer(state = initialState, action: ContactProfileActions) {
    switch (action.type) {
    case actions.SET_ERROR:
        return {
            ...state,
            errors: action.payload
        };
    case actions.PATCH:
        return {
            ...state,
            isPatching: true
        };
    case actions.PATCH_FAIL:
        return {
            ...state,
            errors: buildErrors(action.payload),
            isPatching: false
        };
    case actions.PATCH_SUCCESS:
        return {
            ...state,
            isPatching: false
        };
    case actions.CHANGE_PASSWORD:
        return {
            ...state,
            isChangingPassword: true,
            errors: []
        };
    case actions.CHANGE_PASSWORD_SUCCESS:
        return {
            ...state,
            isChangingPassword: false,
            errors: []
        };
    case actions.CHANGE_PASSWORD_FAIL:
        return {
            ...state,
            isChangingPassword: false,
            errors: buildErrors(action.payload)
        };
    // New Profile API actions
    case actions.GET_PROFILE:
        return {
            ...state,
            isLoadingProfile: true,
            errors: []
        };
    case actions.GET_PROFILE_SUCCESS:
        return {
            ...state,
            isLoadingProfile: false,
            profile: action.payload,
            errors: []
        };
    case actions.GET_PROFILE_FAIL:
        return {
            ...state,
            isLoadingProfile: false,
            errors: buildErrors(action.payload)
        };
    case actions.UPDATE_PROFILE:
        return {
            ...state,
            isUpdatingProfile: true,
            errors: []
        };
    case actions.UPDATE_PROFILE_SUCCESS:
        return {
            ...state,
            isUpdatingProfile: false,
            profile: action.payload,
            errors: []
        };
    case actions.UPDATE_PROFILE_FAIL:
        return {
            ...state,
            isUpdatingProfile: false,
            errors: buildErrors(action.payload)
        };
    case actions.ACCEPT_TERMS_AND_CONDITIONS:
        return {
            ...state,
            isAcceptingTerms: true,
            errors: []
        };
    case actions.ACCEPT_TERMS_AND_CONDITIONS_SUCCESS:
        return {
            ...state,
            isAcceptingTerms: false,
            errors: []
        };
    case actions.ACCEPT_TERMS_AND_CONDITIONS_FAIL:
        return {
            ...state,
            isAcceptingTerms: false,
            errors: buildErrors(action.payload)
        };
    default:
        return state;
    }
}

export const getContactProfileState = createFeatureSelector<ContactProfileState>('contactProfile');

export const getErrors = createSelector(
    getContactProfileState,
    (state: ContactProfileState) => state.errors
);
export const getIsPatching = createSelector(
    getContactProfileState,
    (state: ContactProfileState) => state.isPatching
);

export const getIsChangingPassword = createSelector(
    getContactProfileState,
    (state: ContactProfileState) => state.isChangingPassword
);

export const getIsLoadingProfile = createSelector(
    getContactProfileState,
    (state: ContactProfileState) => state.isLoadingProfile
);

export const getIsUpdatingProfile = createSelector(
    getContactProfileState,
    (state: ContactProfileState) => state.isUpdatingProfile
);

export const getIsAcceptingTerms = createSelector(
    getContactProfileState,
    (state: ContactProfileState) => state.isAcceptingTerms
);

export const getProfile = createSelector(
    getContactProfileState,
    (state: ContactProfileState) => state.profile
);


export class ContactError {
    constructor(
        public status = 500,
        public fieldName = '',
        public message = 'Something went wrong. Please try again') {
    }
}


const buildErrors = (err: any | ApiException): ContactError[] => {

    const status = !!err.status ? err.status : HttpStatus.INTERNAL_SERVER_ERROR;
    if (err instanceof ApiException) {
        const response = JSON.parse(err.response);
        return Object.entries(response.errors).map(([field, messages]) => (
            new ContactError(status, field, (messages as string[]).join(', '))
        ));

    } else if (status === HttpStatus.INTERNAL_SERVER_ERROR) {
        return [new ContactError()];
    } else if (status === HttpStatus.NOT_FOUND) {
        return [new ContactError(
            404,
            '',
            'Contact not found'
        )];
    } else if (status === HttpStatus.BAD_REQUEST) {

        if (!err.error) {
            return [new ContactError()];
        }

        const { messages } = err.error;
        return messages.map (({ message: msg, reference }) => {
            let fldTokens = reference.split('/');
            fldTokens = fldTokens.slice(2, fldTokens.length);
            const key = fldTokens[fldTokens.length - 1];
            const fldName = formControls.CheckOutFormControls[key];
            const fld = fldTokens.join('.');
            return new ContactError(status, fld, msg.replace('%FIELD_NAME%', fldName));
        });
    }

    return [new ContactError()];

};

